import React, { useEffect, useState } from 'react';
import { Table, Pagination, Button, Modal } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';
import InventoryQueryFilters from '../../components/InventoryQueryFilters';
import { useUsers } from '../../context/UserContext';
import { deleteInventoryRedistribution } from '../../services/project/inventoryService';
import { queueDistributionReport } from '../../services/project/reportService';

const getLocalDateInputValue = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
};

const toNumberOrNull = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const getDistributedValue = (item) => {
  return (
    toNumberOrNull(item?.totalDistributedQuantity) ??
    toNumberOrNull(item?.distributedQty) ??
    toNumberOrNull(item?.distributedQuantity) ??
    toNumberOrNull(item?.qtyDistributed)
  );
};

const getConsumedValue = (item) => {
  return (
    toNumberOrNull(item?.totalConsumedQuantity) ??
    toNumberOrNull(item?.consumedQty) ??
    toNumberOrNull(item?.consumedQuantity) ??
    toNumberOrNull(item?.qtyConsumed)
  );
};

const getRemainingValue = (item) => {
  const explicit =
    toNumberOrNull(item?.totalAvailableQuantity) ??
    toNumberOrNull(item?.remainingQty) ??
    toNumberOrNull(item?.remainingQuantity) ??
    toNumberOrNull(item?.availableQty);

  if (explicit !== null) return explicit;

  const distributed = getDistributedValue(item);
  const consumed = getConsumedValue(item);

  if (distributed !== null && consumed !== null) {
    return distributed - consumed;
  }

  return null;
};

const getIssuedValue = (item) => {
  return toNumberOrNull(item?.totalIssuedQuantity);
};

const DistributionsPage = () => {
  const { isSuperAdmin } = useUsers();
  const { projectId } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    region: '',
    area: '',
    startDate: getLocalDateInputValue(),
    endDate: getLocalDateInputValue(),
  });
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [deletingDistributionId, setDeletingDistributionId] = useState(null);
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);



  const formatLocalDateTime = (dateTime) => {
    if (!dateTime) return '-';
    const parsed = new Date(dateTime);
    if (Number.isNaN(parsed.getTime())) return '-';
    const datePart = parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
    const timePart = parsed.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return `${datePart} ${timePart}`;
  };

  const getUserRegionNames = (user) => {
    if (user?.region?.names && Array.isArray(user.region.names)) {
      return user.region.names;
    }
    return [];
  };

  const getUserAreaNames = (user) => {
    if (user?.areas && Array.isArray(user.areas)) {
      return user.areas.map(a => a?.name).filter(Boolean);
    }
    return [];
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/aims/v2/inventory/redistributions', {
        params: {
          page,
          size,
          search: filters.search,
          region: filters.region,
          area: filters.area,
          startDate: filters.startDate,
          endDate: filters.endDate,
        }
      });
      setItems(res?.data?.data || []);
      setTotalPages(res?.data?.totalPages || 1);
      setTotal(res?.data?.total || 0);
    } catch (err) {
      setItems([]);
      setTotalPages(1);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [page, size, filters]);

  const handleDelete = async (id) => {
    if (!id) return;

    const confirmed = window.confirm(
      'Delete this redistribution? This action cannot be undone.\n\n' +
      'This may fail if there are downstream transfers, consumption, or returns,\n' +
      'or if recipient balances were already consumed/transferred.'
    );
    if (!confirmed) return;

    setDeletingDistributionId(id);
    try {
      const res = await deleteInventoryRedistribution(id);
      toast.success(res?.message || 'Redistribution deleted successfully');
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || 'Failed to delete redistribution');
    } finally {
      setDeletingDistributionId(null);
    }
  };

  const handleApplyFilters = (nextFilters) => {
    setPage(0);
    setFilters(nextFilters);
  };

  const handleOpenItemsModal = (record) => {
    setSelectedRecord(record);
    setShowItemsModal(true);
  };

  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    try {
      const res = await queueDistributionReport({
        projectId,
        startDate: filters.startDate,
        endDate: filters.endDate,
      });
      toast.success(res?.message || 'Distribution report queued successfully');
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || 'Failed to generate distribution report');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  return (
    <>
        <h2>Distributions</h2>
        <InventoryQueryFilters
          initialValues={filters}
          onApply={handleApplyFilters}
          isLoading={loading}
        />
        <div className="mb-3 d-flex gap-2">
          <Button
            variant="outline-primary"
            onClick={handleGenerateReport}
            disabled={isGeneratingReport}
          >
            {isGeneratingReport ? 'Queuing...' : 'Generate Report'}
          </Button>
        </div>
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>GI Number</th>
              <th>GI Date</th>
              <th>Deployment Days</th>
              <th>Issuer</th>
              <th>Recipient</th>
              <th>Region</th>
              <th>Created At</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8}>Loading...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8}>No distributions found.</td></tr>
            ) : (
              items.map(redistribution => (
                <tr key={redistribution.id}>
                  <td>{redistribution.originalGINumber}</td>
                  <td>{redistribution.originalGIDate}</td>
                  <td>{redistribution.originalDeploymentDays}</td>
                  <td>{redistribution.issuerFullName}</td>
                  <td>{redistribution.recipientFullName}</td>
                  <td>{getUserRegionNames(redistribution.recipient).join(', ') || '-'}</td>
                  <td>{formatLocalDateTime(redistribution.createdAt)}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button variant="outline-primary" size="sm" onClick={() => handleOpenItemsModal(redistribution)}>
                        View ({Array.isArray(redistribution.items) ? redistribution.items.length : 0})
                      </Button>
                      {isSuperAdmin && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={deletingDistributionId === redistribution.id}
                          onClick={() => handleDelete(redistribution.id)}
                        >
                          {deletingDistributionId === redistribution.id ? 'Deleting...' : 'Delete'}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        <div className="d-flex justify-content-between align-items-center">
          <div>Page: {page + 1} of {totalPages} (Total: {total})</div>
          <Pagination>
            <Pagination.Prev onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} />
            {[...Array(totalPages)].map((_, idx) => (
              <Pagination.Item
                key={idx}
                active={idx === page}
                onClick={() => setPage(idx)}
              >
                {idx + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} />
          </Pagination>
        </div>

        <Modal
          show={showItemsModal}
          onHide={() => {
            setShowItemsModal(false);
            setSelectedRecord(null);
          }}
          centered
          size="xl"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Distribution Details - {selectedRecord?.referenceNo}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <h6>Issuer</h6>
              <div className="mb-2"><strong>Name:</strong> {selectedRecord?.issuer?.fullName}</div>
              <div className="mb-2"><strong>Username:</strong> {selectedRecord?.issuer?.username}</div>
              <div className="mb-2"><strong>Role:</strong> {selectedRecord?.issuer?.role?.[0] || '-'}</div>
              <div className="mb-2"><strong>Region(s):</strong> {getUserRegionNames(selectedRecord?.issuer).join(', ') || '-'}</div>
              <div className="mb-2"><strong>Area(s):</strong> {getUserAreaNames(selectedRecord?.issuer).join(', ') || '-'}</div>
            </div>

            <hr />

            <div className="mb-3">
              <h6>Recipient</h6>
              <div className="mb-2"><strong>Name:</strong> {selectedRecord?.recipient?.fullName}</div>
              <div className="mb-2"><strong>Username:</strong> {selectedRecord?.recipient?.username}</div>
              <div className="mb-2"><strong>Role:</strong> {selectedRecord?.recipient?.role?.[0] || '-'}</div>
              <div className="mb-2"><strong>Region(s):</strong> {getUserRegionNames(selectedRecord?.recipient).join(', ') || '-'}</div>
              <div className="mb-2"><strong>Area(s):</strong> {getUserAreaNames(selectedRecord?.recipient).join(', ') || '-'}</div>
            </div>

            <hr />

            <div className="mb-3">
              <div><strong>Remarks:</strong> {selectedRecord?.remarks || '-'}</div>
            </div>

            <h6>Items</h6>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>MatCode</th>
                  <th>MatLabel</th>
                  <th>MatType</th>
                  <th>Description</th>
                  <th>Issued</th>
                  <th>Distributed</th>
                  <th>Consumed</th>
                  <th>Remaining</th>
                </tr>
              </thead>
              <tbody>
                {!Array.isArray(selectedRecord?.items) || selectedRecord.items.length === 0 ? (
                  <tr><td colSpan={8}>No items found.</td></tr>
                ) : (
                  selectedRecord.items.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.matCode}</td>
                      <td>{item.matLabel}</td>
                      <td>{item.matType}</td>
                      <td>{item.matDescription}</td>
                      <td>{getIssuedValue(item) ?? '-'}</td>
                      <td>{getDistributedValue(item) ?? '-'}</td>
                      <td>{getConsumedValue(item) ?? '-'}</td>
                      <td>{getRemainingValue(item) ?? '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowItemsModal(false);
                setSelectedRecord(null);
              }}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
    </>
  );
};

export default DistributionsPage;
